import { useState, useRef, useCallback } from "react";

interface UseRecordingTimerOptions {
  maxDurationSeconds: number;
  onMaxDurationReached?: () => void;
  onRequestData?: () => void; // Called at max duration to request final MediaRecorder chunk
}

interface UseRecordingTimerReturn {
  timerSeconds: number;
  formattedTimer: string;
  formattedTimerWithMax: string;
  startTimer: () => void;
  stopTimer: () => void;
  resetTimer: () => void;
  stoppedAtTimeLimit: boolean;
  isRunning: boolean;
}

export function isUrgencyThresholdReached(
  timerSeconds: number,
  maxDurationSeconds: number,
  thresholdSeconds = 10
): boolean {
  return timerSeconds >= maxDurationSeconds - thresholdSeconds;
}

/**
 * Hook to manage recording timer with auto-stop at max duration.
 * Handles timer display, interval management, and cleanup.
 */
export function useRecordingTimer({
  maxDurationSeconds,
  onMaxDurationReached,
  onRequestData,
}: UseRecordingTimerOptions): UseRecordingTimerReturn {
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [stoppedAtTimeLimit, setStoppedAtTimeLimit] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const stoppedAtTimeLimitRef = useRef(false);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    startTimeRef.current = null;
    setIsRunning(false);
  }, []);

  const resetTimer = useCallback(() => {
    stopTimer();
    setTimerSeconds(0);
    setStoppedAtTimeLimit(false);
    stoppedAtTimeLimitRef.current = false;
  }, [stopTimer]);

  const startTimer = useCallback(() => {
    // Reset any previous timer state
    stopTimer();
    setTimerSeconds(0);
    setStoppedAtTimeLimit(false);
    stoppedAtTimeLimitRef.current = false;

    startTimeRef.current = Date.now();
    setIsRunning(true);

    timerRef.current = setInterval(() => {
      if (!startTimeRef.current) return;

      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const displayTime = Math.min(elapsed, maxDurationSeconds);
      setTimerSeconds(displayTime);

      // Request final data chunk when timer hits max duration
      // This ensures we capture the full recording before stopping
      if (elapsed >= maxDurationSeconds && elapsed < maxDurationSeconds + 0.1) {
        onRequestData?.();
      }

      // Stop recording after buffer (2.5 seconds) to ensure we capture full duration
      // Buffer accounts for MediaRecorder chunking delays and setup time
      if (elapsed >= maxDurationSeconds + 2.5) {
        stoppedAtTimeLimitRef.current = true;
        setStoppedAtTimeLimit(true);
        stopTimer();
        onMaxDurationReached?.();
      }
    }, 200); // Update every 200ms for smooth timer display
  }, [maxDurationSeconds, onMaxDurationReached, onRequestData, stopTimer]);

  // Format timer display
  const formattedTimer = formatTimer(timerSeconds);
  const formattedTimerWithMax = `${formattedTimer} / ${formatTimer(maxDurationSeconds)}`;

  return {
    timerSeconds,
    formattedTimer,
    formattedTimerWithMax,
    startTimer,
    stopTimer,
    resetTimer,
    stoppedAtTimeLimit,
    isRunning,
  };
}

/**
 * Format seconds as m:ss
 */
function formatTimer(seconds: number): string {
  const clamped = Math.max(0, seconds);
  const mins = Math.floor(clamped / 60);
  const secs = Math.floor(clamped % 60)
    .toString()
    .padStart(2, "0");
  return `${mins}:${secs}`;
}
